import { DetailedList, FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import {
  Alert,
  Box,
  ButtonGroup,
  HighlightContainer,
  Spacer,
  Spinner,
  Stack,
  Typography,
} from '@qbe/ads-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CreditCardForm from '~/components/DigitalPayments/CreditCardForm';
import { UnorderedList, UnorderedListItem } from '~/components-ds/UnorderedList';
import { config, EXIT_URL } from '~/config';
import { TRIGGER_MFE_CREDIT_CARD_SUBMIT } from '~/constants/events';
import { PAY_MY_BILL_CONFIRMATION_ROUTE } from '~/constants/routes';
import { useAppContent, useAppDispatch, usePayments, usePayMyBill } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { clearPaymentErrors } from '~/slices/payments';
import { payMyBillAction } from '~/slices/payments/thunks';
import {
  qalFormCompleteAction,
  qalTrackEventAction,
  qalTrackPageViewEvent,
} from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { analyticsConstant, AnalyticsTransactionType } from '~/types/analytics';
import { getDaysPriorPayment } from '~/utils/analytics/helpers';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

import { PaymentMethod } from './PaymentMethod';
import { RetrievePolicyDetailsForm } from './RetrievePolicyDetailsForm';
import { PaymentState } from './types';

const PayMyBill = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent({ basePath: 'dsme.payMyBill' });

  const [isCcFormValid, setIsCcFormValid] = useState(false);

  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { paymentState: ccPaymentState } = usePayments();

  const { errors } = usePayments();

  const navigate = useAppNavigate();
  const [paymentState, setPaymentState] = useState<PaymentState>('init');

  const { policy } = usePayMyBill();

  const handleFormComplete = useCallback(() => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  }, [dispatch]);
  // Analytics track confirmation page view.
  // Overcome the race condition which update route is not getting updated redux state.
  const trackAnalyticsPageView = useCallback(
    (path: string) => {
      dispatch(qalTrackPageViewEvent(path));
    },
    [dispatch]
  );

  // Analytics track Policy Bill Found.
  useEffect(() => {
    if (policy) {
      handleFormComplete();
      trackAnalyticsPageView('bill-found');
      if (policy) {
        const { dueDate = '' } = policy;
        const dayDifference = getDaysPriorPayment(dueDate);
        // Tracking Days Difference between due Date and Retrieve Policy Date.
        dispatch(
          qalTrackEventAction({
            eventAction: 'prior date retrieve policy',
            eventLabel: dayDifference.toString() || analyticsConstant.NOT_SET,
            data: {
              transactionType: AnalyticsTransactionType.renewalPayment,
            },
          })
        );
      }
    }
  }, [dispatch, handleFormComplete, policy, trackAnalyticsPageView]);

  // Analytics track Payment Details.
  useEffect(() => {
    if (paymentState === 'paying') trackAnalyticsPageView('payment-details');
  }, [paymentState, trackAnalyticsPageView]);

  const onPolicyRetrieved = useCallback((success: boolean) => {
    setPaymentState(success ? 'paying' : 'retry');
  }, []);

  useEffect(() => {
    if (ccPaymentState === 'success') {
      dispatch(
        navigateAction({
          to: PAY_MY_BILL_CONFIRMATION_ROUTE,
          navigate,
        })
      );
    }
  }, [ccPaymentState, dispatch, navigate, trackAnalyticsPageView]);

  const onSubmitPayment = useCallback(async () => {
    dispatch(clearPaymentErrors());
    dispatch(
      payMyBillAction({
        issueDocument: false,
      })
    );
    handleFormComplete();
  }, [dispatch, handleFormComplete]);

  const onExit = () => {
    const origin = searchParams.get('origin');
    if (origin && config.allowedOrigins.includes(origin)) window.location.href = origin;
    else window.location.href = EXIT_URL;
  };

  const handleFormStateChange = (state: { isValid: boolean }) => {
    setIsCcFormValid(state.isValid);
  };

  return (
    <Stack spacing={3} data-testid="pay-my-bill">
      <FormCard
        description={getTextContent('description')}
        title={getTextContent('title')}
        variant="pageTitle"
      />

      <FormCard variant="content">
        <RetrievePolicyDetailsForm
          paymentState={paymentState}
          onComplete={onPolicyRetrieved}
          onExit={onExit}
        />
      </FormCard>

      {policy && (
        <>
          <FormCard variant="content">
            <DetailedList
              list={[
                {
                  id: 'policyType',
                  label: getTextContent('fields.policyDetails.type'),
                  name: 'policyType',
                  value: policy.policyType,
                },
                {
                  id: 'policyNumber',
                  label: getTextContent('fields.policyDetails.policyNumber'),
                  name: 'policyNumber',
                  value: policy.policyNumber,
                },
                {
                  id: 'dueAmount',
                  label: getTextContent('fields.policyDetails.amountDue'),
                  name: 'dueAmount',
                  value: currencyFormatter(policy.dueAmount, CurrencyFormat.CommaDecimal),
                },
              ]}
              variant="multiple"
              title={getTextContent('fields.policyDetails.title')}
            />
          </FormCard>
          <FormCard title={getTextContent('fields.paymentDetails.title')} variant="sectionTitle">
            {paymentState === 'paying' && (
              <>
                <PaymentMethod />
                <Spacer size="md" />
                <div id="credit-card-details" />

                <CreditCardForm
                  onFormStateChange={handleFormStateChange}
                  onSubmitPayment={onSubmitPayment}
                />
                <Box sx={{ maxWidth: '400px' }}>
                  <HighlightContainer>
                    <Typography
                      sx={{
                        marginBottom: 1,
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      {getTextContent('content.otherWaysToPay.title')}
                    </Typography>
                    <Typography variant="body2">
                      {getJSXContent('content.otherWaysToPay.description')}
                    </Typography>
                  </HighlightContainer>
                </Box>
              </>
            )}

            {errors && errors.length > 0 && (
              <Box
                sx={{
                  marginTop: 4,
                  marginBottom: 2,
                  width: '100%',
                  maxWidth: '400px',
                }}
              >
                <Alert variant="error" heading="" sx={{ marginBottom: 1 }}>
                  {errors.length === 1 ? (
                    errors[0].message
                  ) : (
                    <UnorderedList>
                      {errors?.map(({ message }) => (
                        <UnorderedListItem key={message}>{message}</UnorderedListItem>
                      ))}
                    </UnorderedList>
                  )}
                </Alert>
              </Box>
            )}

            <FormBlock preset="button">
              <ButtonGroup
                secondaryBtnProps={{
                  label: getSharedContent('exitButtonLabel'),
                  onClick: onExit,
                }}
                primaryBtnProps={{
                  type: 'submit',
                  label:
                    paymentState === 'init'
                      ? getTextContent('buttons.findPolicy')
                      : getTextContent('buttons.pay'),
                  disabled:
                    (paymentState !== 'selected' && !isCcFormValid) ||
                    ccPaymentState === 'in-progress',
                  onClick:
                    paymentState === 'paying'
                      ? () => {
                          navigate('#credit-card-details');
                          window.dispatchEvent(new Event(TRIGGER_MFE_CREDIT_CARD_SUBMIT));
                        }
                      : undefined,
                  'data-track': 'Submit - pay your bill now',
                }}
              />
            </FormBlock>

            <Spinner
              description={getTextContent('fields.loadingLabel.inProgress')}
              loading={ccPaymentState === 'in-progress'}
            />
          </FormCard>
        </>
      )}
    </Stack>
  );
};

export default PayMyBill;
